import { useState, type FocusEvent, type KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { InlineCompanyForm } from "@/components/forms/InlineCompanyForm";
import { AllergyTagSelector } from "@/components/ui/allergy-tag-selector";
import {
  Person,
  PersonInsert,
  PersonUpdate,
  PersonType,
  useUpdatePerson,
} from "@/hooks/usePeople";
import { useCompanies } from "@/hooks/useCompanies";
import { useAuth } from "@/hooks/useAuth";
import {
  useAllergyTags,
  useSetPersonAllergyTags,
  useCreateAllergyTag,
} from "@/hooks/useAllergyTags";
import { useDraftField } from "@/hooks/useDraftField";
import { toast } from "sonner";

const personTypes: { value: PersonType; label: string }[] = [
  { value: "contact", label: "Contact" },
  { value: "employee", label: "Employee" },
  { value: "freelancer", label: "Freelancer" },
];

const personSchema = z.object({
  number: z.string().max(50, "Number is too long").nullable().optional(),
  first_name: z
    .string()
    .min(1, "First name is required")
    .max(100, "First name is too long"),
  last_name: z
    .string()
    .min(1, "Last name is required")
    .max(100, "Last name is too long"),
  function: z.string().max(255, "Function is too long").nullable().optional(),
  email: z
    .string()
    .email("Invalid email address")
    .max(255, "Email is too long")
    .nullable()
    .optional()
    .or(z.literal("")),
  phone: z.string().max(50, "Phone number is too long").nullable().optional(),
  unity_username: z
    .string()
    .trim()
    .max(100, "Unity username is too long")
    .regex(
      /^[A-Za-z0-9._-]+$/,
      "Unity username can only contain letters, numbers, periods, underscores, and hyphens",
    )
    .nullable()
    .optional()
    .or(z.literal("")),
  person_type: z.enum(["contact", "employee", "freelancer"]),
  company_id: z.string().uuid().nullable().optional(),
  is_admin: z.boolean().optional(),
  remarks: z.string().nullable().optional(),
  allergies_notes: z.string().nullable().optional(),
});

type PersonFormValues = z.infer<typeof personSchema>;

interface PersonFormProps {
  person?: Person | null;
  onSubmit: (
    data: PersonInsert | PersonUpdate,
  ) => void | { id: string } | Promise<void | { id: string }>;
  onCancel: () => void;
  isLoading?: boolean;
  defaultType?: PersonType;
}

export function PersonForm({
  person,
  onSubmit,
  onCancel,
  isLoading,
  defaultType,
}: PersonFormProps) {
  if (person) {
    return <LivePersonForm person={person} />;
  }

  return (
    <CreatePersonForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      isLoading={isLoading}
      defaultType={defaultType}
    />
  );
}

function CreatePersonForm({
  onSubmit,
  onCancel,
  isLoading,
  defaultType,
}: Omit<PersonFormProps, "person">) {
  const { role } = useAuth();
  const { data: companies } = useCompanies();
  const { data: allTags } = useAllergyTags();
  const setPersonTags = useSetPersonAllergyTags();
  const createTag = useCreateAllergyTag();

  const [newPersonTagIds, setNewPersonTagIds] = useState<string[]>([]);
  const selectedTagIds = newPersonTagIds;

  const setSelectedTagIds = (tagIds: string[]) => {
    setNewPersonTagIds(tagIds);
  };

  const handleCreateTag = async (name: string) => {
    const tag = await createTag.mutateAsync(name);
    setSelectedTagIds([...selectedTagIds, tag.id]);
  };

  const companyOptions =
    companies?.map((c) => ({
      value: c.id,
      label: c.name,
    })) ?? [];

  const form = useForm<PersonFormValues>({
    resolver: zodResolver(personSchema),
    defaultValues: {
      number: "",
      first_name: "",
      last_name: "",
      function: "",
      email: "",
      phone: "",
      unity_username: "",
      person_type: defaultType ?? "contact",
      company_id: null,
      is_admin: false,
      remarks: "",
      allergies_notes: "",
    },
  });

  const watchPersonType = form.watch("person_type");

  const handleSubmit = async (values: PersonFormValues) => {
    const payload: PersonInsert | PersonUpdate = {
      number: values.number || null,
      first_name: values.first_name,
      last_name: values.last_name,
      function: values.function || null,
      email: values.email || null,
      phone: values.phone || null,
      unity_username: values.unity_username || null,
      person_type: values.person_type,
      company_id: values.company_id || null,
      remarks: values.remarks || null,
      allergies_notes: values.allergies_notes || null,
    };
    if (role === "admin" && values.is_admin !== undefined) {
      payload.is_admin = values.is_admin;
    }
    const created = await onSubmit(payload);
    if (created && "id" in created && created.id && selectedTagIds.length > 0) {
      await setPersonTags.mutateAsync({
        personId: created.id,
        tagIds: selectedTagIds,
      });
    }
    onCancel();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Number</FormLabel>
              <FormControl>
                <Input
                  placeholder="Unique identifier"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="First name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input
                    placeholder="+49 123 456789"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="unity_username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unity Username</FormLabel>
                <FormControl>
                  <Input
                    placeholder="unity.username"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormDescription>Must be unique across people.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="person_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {personTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="company_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company</FormLabel>
              <FormControl>
                <EntityCombobox
                  options={companyOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select company"
                  searchPlaceholder="Search companies..."
                  emptyMessage="No companies found."
                  createLabel="Create new company"
                  renderCreateForm={({ onSuccess, onCancel, initialName }) => (
                    <InlineCompanyForm
                      onSuccess={onSuccess}
                      onCancel={onCancel}
                      initialName={initialName}
                    />
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="function"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Function</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g. Projektmitarbeiter Weltcup & Events Swiss-Ski"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {role === "admin" && watchPersonType === "employee" && (
          <FormField
            control={form.control}
            name="is_admin"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel>Administrator</FormLabel>
                  <FormDescription>
                    Grant this person full admin access to the system
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <FormItem>
          <Label>Allergies</Label>
          <AllergyTagSelector
            allTags={allTags ?? []}
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
            onCreateTag={handleCreateTag}
          />
        </FormItem>

        <FormField
          control={form.control}
          name="allergies_notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Allergy Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Additional details about allergies..."
                  {...field}
                  value={field.value ?? ""}
                  rows={2}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="remarks"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Remarks</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Additional notes about this person..."
                  {...field}
                  value={field.value ?? ""}
                  rows={2}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Create Person"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

type LivePersonField = keyof Pick<
  PersonUpdate,
  | "number"
  | "first_name"
  | "last_name"
  | "function"
  | "email"
  | "phone"
  | "unity_username"
  | "person_type"
  | "company_id"
  | "is_admin"
  | "remarks"
  | "allergies_notes"
>;

type LiveTextFieldProps = {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
  required?: boolean;
  type?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  pattern?: string;
  title?: string;
  onCommit: (value: string | null) => void | Promise<void>;
};

function normalizeOptionalText(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function LiveTextField({
  label,
  value,
  placeholder,
  required,
  type = "text",
  multiline,
  rows = 2,
  maxLength,
  pattern,
  title,
  onCommit,
}: LiveTextFieldProps) {
  const draft = useDraftField<string, string | null | undefined>({
    value,
    fromShared: (shared) => shared ?? "",
    toShared: (text) => (required ? text.trim() : normalizeOptionalText(text)),
    onCommit,
  });

  const commitOnBlur = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    event.currentTarget.reportValidity();
    if (!event.currentTarget.checkValidity()) return;
    void draft.commit();
  };

  const commitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      draft.cancel();
      return;
    }

    if (event.key !== "Enter") return;
    event.preventDefault();
    event.currentTarget.reportValidity();
    if (!event.currentTarget.checkValidity()) return;
    void draft.commit();
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {multiline ? (
        <Textarea
          placeholder={placeholder}
          value={draft.draft}
          rows={rows}
          required={required}
          maxLength={maxLength}
          onChange={(event) => draft.setDraft(event.target.value)}
          onBlur={commitOnBlur}
        />
      ) : (
        <Input
          type={type}
          placeholder={placeholder}
          value={draft.draft}
          required={required}
          maxLength={maxLength}
          pattern={pattern}
          title={title}
          onChange={(event) => draft.setDraft(event.target.value)}
          onBlur={commitOnBlur}
          onKeyDown={commitOnEnter}
        />
      )}
    </div>
  );
}

type LivePerson = Person & {
  person_allergy_tags?: { allergy_tag_id: string }[];
};

function LivePersonForm({ person }: { person: LivePerson }) {
  const { role } = useAuth();
  const { data: companies } = useCompanies();
  const { data: allTags } = useAllergyTags();
  const updatePerson = useUpdatePerson();
  const setPersonTags = useSetPersonAllergyTags();
  const createTag = useCreateAllergyTag();

  const companyOptions =
    companies?.map((c) => ({
      value: c.id,
      label: c.name,
    })) ?? [];

  const selectedTagIds =
    person.person_allergy_tags?.map((pt) => pt.allergy_tag_id) ?? [];
  const watchPersonType = person.person_type;

  const updateField = async <K extends LivePersonField>(
    field: K,
    value: PersonUpdate[K],
  ) => {
    if (person[field as keyof Person] === value) return;

    try {
      await updatePerson.mutateAsync({
        id: person.id,
        updates: { [field]: value } as PersonUpdate,
      });
    } catch (error) {
      toast.error(
        "Failed to update person: " +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  };

  const setSelectedTagIds = (tagIds: string[]) => {
    setPersonTags.mutate({
      personId: person.id,
      tagIds,
    });
  };

  const handleCreateTag = async (name: string) => {
    const tag = await createTag.mutateAsync(name);
    setSelectedTagIds([...selectedTagIds, tag.id]);
  };

  return (
    <div className="space-y-6">
      <LiveTextField
        label="Number"
        value={person.number}
        placeholder="Unique identifier"
        maxLength={50}
        onCommit={(value) => updateField("number", value)}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <LiveTextField
          label="First Name *"
          value={person.first_name}
          required
          placeholder="First name"
          maxLength={100}
          onCommit={(value) => updateField("first_name", value ?? "")}
        />
        <LiveTextField
          label="Last Name *"
          value={person.last_name}
          required
          placeholder="Last name"
          maxLength={100}
          onCommit={(value) => updateField("last_name", value ?? "")}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <LiveTextField
          label="Email"
          value={person.email}
          type="email"
          placeholder="email@example.com"
          maxLength={255}
          onCommit={(value) => updateField("email", value)}
        />
        <LiveTextField
          label="Phone"
          value={person.phone}
          placeholder="+49 123 456789"
          maxLength={50}
          onCommit={(value) => updateField("phone", value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <LiveTextField
          label="Unity Username"
          value={person.unity_username}
          placeholder="unity.username"
          maxLength={100}
          pattern="[A-Za-z0-9._-]+"
          title="Unity username can only contain letters, numbers, periods, underscores, and hyphens"
          onCommit={(value) => updateField("unity_username", value)}
        />
      </div>

      <div className="space-y-2">
        <Label>Type *</Label>
        <Select
          value={person.person_type}
          onValueChange={(value: PersonType) =>
            void updateField("person_type", value)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {personTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Company</Label>
        <EntityCombobox
          options={companyOptions}
          value={person.company_id}
          onChange={(value) => void updateField("company_id", value)}
          placeholder="Select company"
          searchPlaceholder="Search companies..."
          emptyMessage="No companies found."
          createLabel="Create new company"
          renderCreateForm={({ onSuccess, onCancel, initialName }) => (
            <InlineCompanyForm
              onSuccess={onSuccess}
              onCancel={onCancel}
              initialName={initialName}
            />
          )}
        />
      </div>

      <LiveTextField
        label="Function"
        value={person.function}
        placeholder="e.g. Projektmitarbeiter Weltcup & Events Swiss-Ski"
        maxLength={255}
        onCommit={(value) => updateField("function", value)}
      />

      {role === "admin" && watchPersonType === "employee" && (
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label>Administrator</Label>
            <p className="text-sm text-muted-foreground">
              Grant this person full admin access to the system
            </p>
          </div>
          <Switch
            checked={person.is_admin ?? false}
            onCheckedChange={(value) => void updateField("is_admin", value)}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>Allergies</Label>
        <AllergyTagSelector
          allTags={allTags ?? []}
          selectedTagIds={selectedTagIds}
          onChange={setSelectedTagIds}
          onCreateTag={handleCreateTag}
        />
      </div>

      <LiveTextField
        label="Allergy Notes"
        value={person.allergies_notes}
        placeholder="Additional details about allergies..."
        multiline
        onCommit={(value) => updateField("allergies_notes", value)}
      />

      <LiveTextField
        label="Remarks"
        value={person.remarks}
        placeholder="Additional notes about this person..."
        multiline
        onCommit={(value) => updateField("remarks", value)}
      />
    </div>
  );
}
